// Backend runtime info captured from API response headers.
// Axios lowercases header names. Sensitive headers (db, debug) are only
// present on authenticated responses; missing ones are stored as null.

export const RUNTIME_INFO_CHANGED_EVENT = "fes-runtime-info-changed";

const EMPTY_RUNTIME_INFO = {
  environment: null,
  version: null,
  db: null,
  debug: null,
  serverTime: null,
  lastSeenAt: null,
};

let current = { ...EMPTY_RUNTIME_INFO };

function readHeader(headers, name) {
  if (!headers || typeof headers !== "object") return null;

  let value;
  if (typeof headers.get === "function") {
    value = headers.get(name);
  }
  if (value == null) {
    value = headers[name];
  }
  if (value == null) {
    const match = Object.keys(headers).find((key) => key.toLowerCase() === name);
    if (match) value = headers[match];
  }
  if (Array.isArray(value)) value = value[0];
  if (value == null || typeof value === "function") return null;

  const text = String(value).trim();
  return text === "" ? null : text;
}

/** Parse x-app-* / x-server-time headers. Returns null when none are present. */
export function parseRuntimeHeaders(headers) {
  if (!headers) return null;

  const parsed = {
    environment: readHeader(headers, "x-app-environment"),
    version: readHeader(headers, "x-app-version"),
    db: readHeader(headers, "x-app-db"),
    debug: readHeader(headers, "x-app-debug"),
    serverTime: readHeader(headers, "x-server-time"),
  };

  const hasAny = Object.values(parsed).some((value) => value != null);
  return hasAny ? parsed : null;
}

function dispatchChanged() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(RUNTIME_INFO_CHANGED_EVENT));
}

export function getRuntimeInfo() {
  return { ...current };
}

export function subscribe(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") {
    return () => {};
  }
  const handler = () => listener(getRuntimeInfo());
  window.addEventListener(RUNTIME_INFO_CHANGED_EVENT, handler);
  return () => window.removeEventListener(RUNTIME_INFO_CHANGED_EVENT, handler);
}

/**
 * Update the store from a success response or error.response.
 * Responses with no runtime headers leave the store unchanged.
 */
export function captureRuntimeHeaders(headers, seenAt = Date.now()) {
  const parsed = parseRuntimeHeaders(headers);
  if (!parsed) return getRuntimeInfo();

  current = {
    environment: parsed.environment,
    version: parsed.version,
    db: parsed.db,
    debug: parsed.debug,
    serverTime: parsed.serverTime,
    lastSeenAt: seenAt,
  };
  dispatchChanged();
  return getRuntimeInfo();
}

export function resetRuntimeInfo() {
  current = { ...EMPTY_RUNTIME_INFO };
  dispatchChanged();
}
